import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Input, Label, Checkbox } from '../design-system';
import {
  submitOrganizationSignup,
  getPlatformPolicies,
  PlatformPolicy,
} from '../services/consentApi';
import '../design-system/styles.css';

interface SignupFormData {
  // Organization details
  organizationName: string;
  organizationWebsite: string;
  organizationType: string;
  
  // Contact person
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contactRole: string;
  
  // Additional info
  expectedUsers: string;
  useCase: string;
  
  // Consent
  privacyPolicyAccepted: boolean;
  termsOfServiceAccepted: boolean;
}

/**
 * Organization signup page for new tenant requests.
 * Route: /signup
 * 
 * This creates a pending request that:
 * 1. Sends notification email to configured admin email
 * 2. Creates a TenantSignupRequest record for super admin review
 * 3. Super admin can approve/reject from their dashboard
 * 4. On approval, tenant is created and admin user receives invite
 */
const OrganizationSignupPage: React.FC = () => {
  const [formData, setFormData] = useState<SignupFormData>({
    organizationName: '',
    organizationWebsite: '',
    organizationType: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    contactRole: '',
    expectedUsers: '',
    useCase: '',
    privacyPolicyAccepted: false,
    termsOfServiceAccepted: false,
  });
  
  const [policies, setPolicies] = useState<PlatformPolicy[]>([]);
  const [expandedPolicy, setExpandedPolicy] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Fetch platform policies
  useEffect(() => {
    getPlatformPolicies()
      .then(setPolicies)
      .catch(err => console.error('Failed to fetch policies:', err));
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!formData.organizationName.trim()) {
      setError('Organization name is required');
      return;
    }
    if (!formData.contactName.trim()) {
      setError('Contact name is required');
      return;
    }
    if (!formData.contactEmail.trim()) {
      setError('Contact email is required');
      return;
    }
    if (!formData.privacyPolicyAccepted || !formData.termsOfServiceAccepted) {
      setError('You must accept the Privacy Policy and Terms of Service');
      return;
    }
    
    setLoading(true);
    
    try {
      await submitOrganizationSignup({
        organizationName: formData.organizationName.trim(),
        organizationWebsite: formData.organizationWebsite.trim() || undefined,
        organizationType: formData.organizationType || undefined,
        contactName: formData.contactName.trim(),
        contactEmail: formData.contactEmail.trim(),
        contactPhone: formData.contactPhone.trim() || undefined,
        contactRole: formData.contactRole.trim() || undefined,
        expectedUsers: formData.expectedUsers || undefined,
        useCase: formData.useCase.trim() || undefined,
        privacyPolicyAccepted: formData.privacyPolicyAccepted,
        termsOfServiceAccepted: formData.termsOfServiceAccepted,
      });
      
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (submitted) {
    return (
      <main className="ds-container">
        <div style={{ maxWidth: 520, margin: '0 auto' }}>
          <Card>
            <div className="ds-alert ds-alert--success" role="alert" style={{ marginBottom: 16 }}>
              ✓ Request submitted successfully!
            </div>
            <h1 className="ds-heading-xl" style={{ marginBottom: 8 }}>Thank you for your interest!</h1>
            <p className="ds-text-muted" style={{ marginBottom: 16 }}>
              Your organization signup request has been received. Our team will review your 
              application and get back to you within 2-3 business days.
            </p>
            <p className="ds-text-muted" style={{ marginBottom: 24 }}>
              A confirmation email has been sent to <strong>{formData.contactEmail}</strong>.
            </p>
            <Link to="/login">
              <Button variant="ghost">Back to Login</Button>
            </Link>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="ds-container" aria-labelledby="signup-title">
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <Card>
          <header className="ds-card__header" style={{ alignItems: 'flex-start' }}>
            <div>
              <p className="ds-text-muted" style={{ margin: 0, fontWeight: 600 }}>Consent Platform</p>
              <h1 id="signup-title" className="ds-heading-xl">Request Organization Access</h1>
              <p className="ds-text-muted" style={{ marginTop: 6 }}>
                Join the Consent Platform to manage consent for your organization and partners. 
                Our team will review your request and set up your tenant environment.
              </p>
            </div>
          </header>

          {error && (
            <div className="ds-alert ds-alert--error" role="alert" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}

          <form className="ds-form" onSubmit={handleSubmit} noValidate>
            {/* Organization Details Section */}
            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <legend style={{ fontWeight: 600, marginBottom: 12, color: 'var(--ds-text-primary)' }}>
                Organization Details
              </legend>
              
              <div className="ds-field">
                <Label htmlFor="organizationName">Organization Name *</Label>
                <Input
                  type="text"
                  id="organizationName"
                  name="organizationName"
                  value={formData.organizationName}
                  onChange={handleChange}
                  placeholder="Acme Corporation"
                  required
                />
              </div>

              <div className="ds-field">
                <Label htmlFor="organizationWebsite">Website</Label>
                <Input
                  type="url"
                  id="organizationWebsite"
                  name="organizationWebsite"
                  value={formData.organizationWebsite}
                  onChange={handleChange}
                  placeholder="https://www.example.com"
                />
              </div>

              <div className="ds-field">
                <Label htmlFor="organizationType">Organization Type</Label>
                <select
                  id="organizationType"
                  name="organizationType"
                  value={formData.organizationType}
                  onChange={handleChange}
                  className="ds-input"
                  style={{ appearance: 'auto' }}
                >
                  <option value="">Select type...</option>
                  <option value="enterprise">Enterprise</option>
                  <option value="startup">Startup</option>
                  <option value="government">Government</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="education">Education</option>
                  <option value="nonprofit">Non-profit</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </fieldset>

            {/* Contact Person Section */}
            <fieldset style={{ border: 'none', padding: 0, margin: '16px 0 0 0' }}>
              <legend style={{ fontWeight: 600, marginBottom: 12, color: 'var(--ds-text-primary)' }}>
                Contact Person
              </legend>
              
              <div className="ds-grid ds-grid--two">
                <div className="ds-field">
                  <Label htmlFor="contactName">Full Name *</Label>
                  <Input
                    type="text"
                    id="contactName"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div className="ds-field">
                  <Label htmlFor="contactRole">Role / Title</Label>
                  <Input
                    type="text"
                    id="contactRole"
                    name="contactRole"
                    value={formData.contactRole}
                    onChange={handleChange}
                    placeholder="CTO, Data Privacy Officer, etc."
                  />
                </div>
              </div>

              <div className="ds-grid ds-grid--two">
                <div className="ds-field">
                  <Label htmlFor="contactEmail">Email *</Label>
                  <Input
                    type="email"
                    id="contactEmail"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required
                  />
                </div>

                <div className="ds-field">
                  <Label htmlFor="contactPhone">Phone</Label>
                  <Input
                    type="tel"
                    id="contactPhone"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                  />
                </div>
              </div>
            </fieldset>

            {/* Additional Info Section */}
            <fieldset style={{ border: 'none', padding: 0, margin: '16px 0 0 0' }}>
              <legend style={{ fontWeight: 600, marginBottom: 12, color: 'var(--ds-text-primary)' }}>
                Additional Information
              </legend>
              
              <div className="ds-field">
                <Label htmlFor="expectedUsers">Expected Number of Users</Label>
                <select
                  id="expectedUsers"
                  name="expectedUsers"
                  value={formData.expectedUsers}
                  onChange={handleChange}
                  className="ds-input"
                  style={{ appearance: 'auto' }}
                >
                  <option value="">Select range...</option>
                  <option value="1-10">1-10</option>
                  <option value="11-50">11-50</option>
                  <option value="51-200">51-200</option>
                  <option value="201-1000">201-1000</option>
                  <option value="1000+">1000+</option>
                </select>
              </div>

              <div className="ds-field">
                <Label htmlFor="useCase">Use Case / Requirements</Label>
                <textarea
                  id="useCase"
                  name="useCase"
                  value={formData.useCase}
                  onChange={handleChange}
                  placeholder="Tell us about your consent management needs, compliance requirements, etc."
                  className="ds-input ds-textarea"
                  rows={3}
                />
              </div>
            </fieldset>

            {/* Platform Consent Section */}
            <div className="ds-stack" style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--ds-border)' }}>
              <p className="ds-text-muted" style={{ margin: 0, fontSize: 14, fontWeight: 500 }}>
                By submitting this request, you agree to:
              </p>
              
              {policies.map(policy => (
                <div key={policy.policyType} style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <Checkbox
                      id={`consent-${policy.policyType}`}
                      checked={
                        policy.policyType === 'PRIVACY_POLICY' 
                          ? formData.privacyPolicyAccepted 
                          : formData.termsOfServiceAccepted
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const key = policy.policyType === 'PRIVACY_POLICY' 
                          ? 'privacyPolicyAccepted' 
                          : 'termsOfServiceAccepted';
                        setFormData(prev => ({ ...prev, [key]: e.target.checked }));
                      }}
                    />
                    <label 
                      htmlFor={`consent-${policy.policyType}`}
                      style={{ flex: 1, cursor: 'pointer', fontSize: 14 }}
                    >
                      <span>I agree to the </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setExpandedPolicy(expandedPolicy === policy.policyType ? null : policy.policyType);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--ds-accent)',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          padding: 0,
                          font: 'inherit',
                        }}
                      >
                        {policy.title}
                      </button>
                      <span style={{ color: 'var(--ds-danger)' }}> *</span>
                    </label>
                  </div>
                  
                  {expandedPolicy === policy.policyType && (
                    <div 
                      style={{
                        marginTop: 8,
                        marginLeft: 26,
                        padding: 12,
                        background: 'var(--ds-surface-muted)',
                        borderRadius: 'var(--ds-radius-md)',
                        fontSize: 13,
                        maxHeight: 200,
                        overflow: 'auto',
                      }}
                      dangerouslySetInnerHTML={{ __html: policy.content }}
                    />
                  )}
                </div>
              ))}
            </div>

            <Button type="submit" variant="primary" isLoading={loading} disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Request'}
            </Button>
          </form>

          <footer className="ds-card__footer" style={{ textAlign: 'center' }}>
            <p className="ds-text-muted" style={{ margin: 0 }}>
              Already have an account?{' '}
              <Link to="/login" style={{ color: 'var(--ds-accent)' }}>
                Sign in
              </Link>
            </p>
          </footer>
        </Card>
      </div>
    </main>
  );
};

export default OrganizationSignupPage;
